import { NoPermissionState } from "@/components/common/no-permission-state";
import { isForbiddenError } from "@/lib/clinic-grants";
import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { LayoutGrid, List, Pencil, Plus, Search, Trash } from "lucide-react";

import {
  AlertDialogDelete,
  FormFinanceAccount,
  Pagination,
} from "@/components/common";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Skeleton,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Segmented,
} from "@/components/ui";

import { useFetchFinanceAccountsQuery, usePermission } from "@/hooks";
import { defaultFinancialAccounts } from "@/helpers";
import { FinanceAccount as FinanceAccountType } from "@/types";
import { formatAvatarName, formatValue, removeLastLetter } from "@/utils";
import { cn } from "@/lib";

type ViewMode = "card" | "list";

export function FinanceAccount() {
  const { t } = useTranslation();
  const { checkPermission } = usePermission();

  const language = i18n.language === "en" ? "en-US" : "pt-BR";

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page");

  const perPageParam = searchParams.get("per_page");
  const [per_page] = useState<number>(perPageParam ? Number(perPageParam) : 10);
  const [search, setSearch] = useState<string>("");
  const [view, setView] = useState<ViewMode>("card");

  const {
    error: listError,
    data: financialAccountsData,
    refetch: refetchFinancialAccountsData,
    isLoading,
  } = useFetchFinanceAccountsQuery({
    page,
    per_page,
    search,
  });

  const [documentsDataAll, setDocumentsDataAll] = useState<
    FinanceAccountType[] | undefined
  >(financialAccountsData?.items);

  useEffect(() => {
    const fetchData = async () => {
      const fetchedData = await refetchFinancialAccountsData();
      setDocumentsDataAll(fetchedData.data?.items);
    };

    fetchData();
  }, [refetchFinancialAccountsData, financialAccountsData, page, per_page, search]);

  const accountName = (account: FinanceAccountType) =>
    defaultFinancialAccounts.includes(account.name)
      ? t(`LABEL.${account.name.toLowerCase()}`)
      : account.name;

  const canCreate = checkPermission("finance account", "create finance account");
  const accounts = Array.isArray(documentsDataAll) ? documentsDataAll : [];
  const loading = isLoading || !documentsDataAll;

  // SEC-GATES-02: a refusal from the list endpoint is stated, not shown as an empty list.
  if (isForbiddenError(listError)) return <NoPermissionState />;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Financeiro"
        title="Contas"
        lede="Saldo, previsto e lançamentos em atraso de cada conta da clínica."
        actions={
          <FormFinanceAccount>
            <Button variant="petrol" size="lg" className="px-5" disabled={!canCreate}>
              <Plus className="size-4" aria-hidden="true" />
              Nova conta
            </Button>
          </FormFinanceAccount>
        }
      />

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:w-80">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-app-fg-subtle"
            aria-hidden="true"
          />
          <Input
            type="search"
            aria-label="Pesquisar contas"
            placeholder="Nome da conta"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            showError={false}
          />
        </div>
        <Segmented
          aria-label="Modo de exibição"
          value={view}
          onChange={setView}
          options={[
            { value: "card", icon: LayoutGrid, ariaLabel: "Cartões" },
            { value: "list", icon: List, ariaLabel: "Lista" },
          ]}
        />
      </div>

      {loading ? (
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-64 rounded-xl" />
          ))}
        </section>
      ) : accounts.length === 0 ? (
        <EmptyState
          title={search ? "Nenhuma conta com esse nome" : "Nenhuma conta ainda"}
          description={
            search
              ? "Tente outro nome ou limpe a busca."
              : "Crie a primeira conta para começar a registrar entradas e saídas."
          }
          action={
            !search && (
              <FormFinanceAccount>
                <Button variant="secondary" disabled={!canCreate}>
                  Criar conta
                </Button>
              </FormFinanceAccount>
            )
          }
        />
      ) : view === "card" ? (
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {accounts.map((account) => (
            <article
              key={account.id}
              className="flex flex-col gap-5 rounded-xl border border-app-border bg-app-surface p-5 shadow-premium-xs"
            >
              <header className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-lg font-semibold tracking-tight text-app-fg-strong">
                    {accountName(account)}
                  </h3>
                  <div className="mt-1.5 flex items-center gap-2 text-sm text-app-fg-muted">
                    <Avatar className="size-6">
                      <AvatarImage src={account.avatar || ""} alt="" />
                      <AvatarFallback className="bg-brand-soft text-[11px] font-medium text-brand">
                        {formatAvatarName(account.user) || "C"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate">{account.user || "Conta da clínica"}</span>
                    <span aria-hidden="true">·</span>
                    <span className="shrink-0">
                      {account.shared ? "Compartilhada" : "Só você"}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <FormFinanceAccount id={account.id}>
                    <Button variant="ghost" size="icon" className="size-8 text-app-fg-muted hover:text-app-fg-strong" aria-label={t("LABEL.edit")}>
                      <Pencil className="size-3.5" />
                    </Button>
                  </FormFinanceAccount>
                  <AlertDialogDelete
                    endpoint={`/finance-accounts/${account.id}`}
                    queryKey="finance-accounts"
                  >
                    <Button variant="ghost" size="icon" className="size-8 text-app-fg-muted hover:text-app-danger" aria-label={t("LABEL.delete")}>
                      <Trash className="size-3.5" />
                    </Button>
                  </AlertDialogDelete>
                </div>
              </header>

              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-app-fg-muted">
                <span className="font-medium text-app-fg">
                  {account.bank?.name ?? "Sem banco"}
                </span>
                <span aria-hidden="true">·</span>
                <span>{account.type?.name ?? "Tipo não informado"}</span>
                {account.agency || account.number ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="tabular-nums">
                      {[account.agency && `Ag. ${account.agency}`, account.number && `Cc ${account.number}`]
                        .filter(Boolean)
                        .join(" / ")}
                    </span>
                  </>
                ) : null}
              </p>

              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-app-fg-muted">
                    {t("LABEL.balance")}
                  </dt>
                  <dd className="mt-1 font-display text-[1.75rem] font-medium leading-none tracking-[-0.01em] text-app-fg-strong tabular-nums">
                    {formatValue(account.balance)}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-app-fg-muted">
                    {t("LABEL.expected")}
                  </dt>
                  <dd className="mt-1 text-lg font-medium leading-none text-app-fg tabular-nums">
                    {formatValue(account.projected_balance)}
                  </dd>
                </div>
              </dl>

              <p
                className={cn(
                  "flex items-center gap-2 text-sm",
                  account.overdue > 0 ? "text-app-fg-strong" : "text-app-fg-muted"
                )}
              >
                <span
                  className={cn(
                    "size-2 shrink-0 rounded-full",
                    account.overdue > 0 ? "bg-app-danger" : "bg-app-success"
                  )}
                  aria-hidden="true"
                />
                {account.overdue > 0
                  ? `${account.overdue} ${account.overdue === 1 ? "lançamento" : "lançamentos"} em atraso`
                  : "Nada em atraso"}
              </p>

              <Button asChild variant="secondary" className="mt-auto w-full">
                <Link to={`/financial/account/${account.id}/entries`}>
                  {t("LABEL.access entries")}
                </Link>
              </Button>
            </article>
          ))}
        </section>
      ) : (
        <section className="overflow-hidden rounded-xl border border-app-border bg-app-surface">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("LABEL.name")}</TableHead>
                <TableHead>
                  {removeLastLetter(t("LABEL.employees"), language === "en-US" ? 2 : 1)}
                </TableHead>
                <TableHead>{t("LABEL.privacy")}</TableHead>
                <TableHead className="text-right">{t("LABEL.balance")}</TableHead>
                <TableHead className="text-right">{t("LABEL.expected_balance")}</TableHead>
                <TableHead>{t("LABEL.delayed_entries")}</TableHead>
                <TableHead className="sr-only">{t("LABEL.view options")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell className="font-medium text-app-fg-strong">{accountName(account)}</TableCell>
                  <TableCell>{account.user || "Conta da clínica"}</TableCell>
                  <TableCell>{account.shared ? "Compartilhada" : "Só você"}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatValue(account.balance)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatValue(account.projected_balance)}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-sm">
                      <span
                        className={cn(
                          "size-1.5 rounded-full",
                          account.overdue > 0 ? "bg-app-danger" : "bg-app-success"
                        )}
                        aria-hidden="true"
                      />
                      <span className="tabular-nums">{account.overdue}</span>
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger className="rounded-lg px-2 py-1 text-sm text-app-fg-muted hover:bg-app-surface-muted hover:text-app-fg-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand">
                        {t("LABEL.view options")}
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-44 rounded-xl border border-app-border p-1">
                        <FormFinanceAccount id={account.id}>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                            {t("LABEL.edit")}
                          </DropdownMenuItem>
                        </FormFinanceAccount>
                        <DropdownMenuItem asChild>
                          <Link to={`/financial/account/${account.id}/entries`}>
                            {t("LABEL.access entries")}
                          </Link>
                        </DropdownMenuItem>
                        <AlertDialogDelete
                          endpoint={`/finance-accounts/${account.id}`}
                          queryKey="finance-accounts"
                        >
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                            className="text-app-danger focus:text-app-danger"
                          >
                            {t("LABEL.delete")}
                          </DropdownMenuItem>
                        </AlertDialogDelete>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      {financialAccountsData?.data && !loading && accounts.length > 0 && (
        <Pagination data={financialAccountsData.data} />
      )}
    </div>
  );
}
